import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, LeadRequest } from './crmApi';
import { TypePick } from './ManagerCrm';
import { Empty, Head, Spinner, btnGold, fmtPhone, fmtWhen, goldBg, inputCls, labelCls, purpleBg, useCrmMe } from './ui';

// ── Lead requests ────────────────────────────────────────────
// A salesperson adds a lead of their own (a referral, someone who called them
// directly); a Lead Manager, Sales Admin or the admin approves it, and then
// it's that salesperson's lead and stays with them.

const STATUS_STYLE: Record<LeadRequest['status'], { label: string; cls: string }> = {
  Pending:  { label: 'Waiting for approval', cls: 'bg-amber-100 text-amber-800' },
  Approved: { label: 'Approved', cls: 'bg-green-100 text-green-800' },
  Rejected: { label: 'Not approved', cls: 'bg-red-100 text-red-700' },
};
const DID: Record<string, string> = {
  new: 'added as your lead',
  moved: 'moved to you',
  reopened: 'opened again for you',
  'already-theirs': 'already yours',
};

const StatusChip: React.FC<{ status: LeadRequest['status'] }> = ({ status }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[status].cls}`}>{STATUS_STYLE[status].label}</span>
);

// What approving will do, for the person deciding
function whatApprovingDoes(r: LeadRequest): { text: string; warn: boolean } {
  const e = r.existing;
  if (!e) return { text: `New number: it becomes ${r.byName}'s lead.`, warn: false };
  if (e.ownerId === r.byId) return { text: `Already ${r.byName}'s lead (#${e.leadNo}, ${e.stage}).`, warn: false };
  if (e.stage === 'Converted') return { text: `Already converted (#${e.leadNo}). It can't be approved.`, warn: true };
  if (e.stage === 'Cold Lead' || e.stage === 'Not Interested') return { text: `Lead #${e.leadNo} is ${e.stage}${e.ownerName ? ` with ${e.ownerName}` : ''}. Approving opens it again for ${r.byName}.`, warn: true };
  return { text: `Already lead #${e.leadNo}${e.ownerName ? ` with ${e.ownerName}` : ', with no one'} (${e.stage}). Approving moves it to ${r.byName}.`, warn: true };
}

// The salesperson: ask to add a lead, and see what happened to each request
export const CrmRequestLead: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const blank = { phone: '', name: '', course: '', mode: '', notes: '', leadType: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState<LeadRequest[] | null>(null);
  const set = (k: keyof typeof blank, v: string) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try { setList((await crm.myLeadRequests(auth)).requests); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.phone.trim()) { toast.error('Enter the phone number'); return; }
    if (!form.leadType) { toast.error('Pick WhatsApp lead or Form lead'); return; }
    setSaving(true);
    try {
      await crm.requestLead(auth, form);
      toast.success('Sent for approval. You will get a notification when it is decided.');
      setForm({ ...blank, leadType: form.leadType });
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  if (!me) return <Spinner />;
  return (
    <div className="space-y-5">
      {!embedded && <Head title="Add a Lead" sub="A lead of your own (a referral, someone who called you). The Lead Manager approves it, then it's yours." />}
      <form onSubmit={send} className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
        {embedded && (
          <div>
            <p className="font-bold text-gray-800">Add a lead of your own</p>
            <p className="text-xs text-gray-400 mt-0.5">A referral, someone who called you. The Lead Manager approves it, then it is yours and stays with you.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={labelCls} htmlFor="r-phone">Phone *</label><input id="r-phone" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="98765 43210" inputMode="tel" className={inputCls} /></div>
          <div><label className={labelCls} htmlFor="r-name">Name</label><input id="r-name" value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} /></div>
          <div>
            <label className={labelCls} htmlFor="r-course">Course</label>
            <select id="r-course" value={form.course} onChange={e => set('course', e.target.value)} className={inputCls}>
              <option value="">—</option>{me.lists.courses.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="r-mode">Online / offline</label>
            <select id="r-mode" value={form.mode} onChange={e => set('mode', e.target.value)} className={inputCls}>
              <option value="">Not known yet</option><option value="Online">Online</option><option value="Offline">Offline</option>
            </select>
          </div>
          <div className="sm:col-span-2"><TypePick label="Lead type *" value={form.leadType} onPick={v => set('leadType', v)} /></div>
          <div className="sm:col-span-2"><label className={labelCls} htmlFor="r-notes">Where the lead came from</label><input id="r-notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g. referred by a student, called me directly" className={inputCls} /></div>
        </div>
        <button type="submit" disabled={saving} className={btnGold} style={goldBg}>{saving ? 'Sending…' : 'Send for approval'}</button>
      </form>

      <div className="space-y-2">
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide px-1">My lead requests {list && <span className="text-gray-400">· {list.length}</span>}</h2>
        {!list ? <Spinner /> : list.length === 0 ? <Empty icon="📝" title="No requests yet" sub="Leads you send for approval show up here." /> : list.map(r => (
          <div key={r.id} className="bg-white border border-gray-100 rounded-2xl px-4 py-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-gray-800">{r.name || 'No name'}</span>
              <span className="text-xs text-gray-500">{fmtPhone(r.phone)}</span>
              <StatusChip status={r.status} />
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">Sent {fmtWhen(r.createdAt)}{r.leadType ? ` · ${r.leadType} lead` : ''}{r.course ? ` · ${r.course}` : ''}</p>
            {r.status === 'Approved' && <p className="text-xs text-green-700 mt-1">Lead #{r.leadNo} {DID[r.outcome] || 'is yours'} · by {r.decidedByName}</p>}
            {r.status === 'Rejected' && <p className="text-xs text-red-600 mt-1">Not approved by {r.decidedByName}{r.reason ? `: ${r.reason}` : ''}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// The Lead Manager, Sales Admin or the admin: approve or turn down
export const CrmLeadRequests: React.FC<{ auth: CrmAuth; embedded?: boolean; onChanged?: () => void }> = ({ auth, embedded, onChanged }) => {
  const [status, setStatus] = useState<'Pending' | 'Approved' | 'Rejected'>('Pending');
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.leadRequests>> | null>(null);
  const [busy, setBusy] = useState('');
  const [turningDown, setTurningDown] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try { setData(await crm.leadRequests(auth, status)); } catch (e: any) { toast.error(e.message); }
  }, [auth, status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (r: LeadRequest, approve: boolean) => {
    setBusy(r.id);
    try {
      const res = await crm.decideLeadRequest(auth, r.id, approve, approve ? '' : reason.trim());
      toast.success(approve ? `${r.name || fmtPhone(r.phone)}: lead #${res.request.leadNo} is ${r.byName}'s now` : `Turned down. ${r.byName} will be told.`);
      setTurningDown(null); setReason('');
      load();
      onChanged?.();
    } catch (e: any) { toast.error(e.message); load(); }
    finally { setBusy(''); }
  };

  const tabs: [typeof status, string][] = [['Pending', 'Waiting'], ['Approved', 'Approved'], ['Rejected', 'Turned down']];
  return (
    <div className="space-y-4">
      <Head embedded={embedded} title="Lead Requests" sub="Leads salespeople added themselves. Approved, the lead is theirs and stays with them." />
      <div className="flex gap-1 bg-white rounded-xl border border-gray-100 p-1 w-fit" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={status === k} onClick={() => setStatus(k)} className="px-3 py-1.5 rounded-lg text-xs font-bold"
            style={status === k ? { background: '#2b2240', color: '#fff' } : { color: '#6b7280' }}>
            {label} {data && <span className="opacity-60">{data.counts[k]}</span>}
          </button>
        ))}
      </div>
      {!data ? <Spinner /> : data.requests.length === 0 ? (
        <Empty icon={status === 'Pending' ? '✅' : '📭'} title={status === 'Pending' ? 'Nothing waiting' : 'None yet'} />
      ) : data.requests.map(r => {
        const what = whatApprovingDoes(r);
        return (
          <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-gray-800">{r.name || 'No name'}</span>
                  <span className="text-sm text-gray-500 font-mono">{fmtPhone(r.phone)}</span>
                  {r.status !== 'Pending' && <StatusChip status={r.status} />}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  From <b className="text-gray-700">{r.byName}</b> · {fmtWhen(r.createdAt)}
                  {r.leadType ? ` · ${r.leadType} lead` : ''}{r.course ? ` · ${r.course}` : ''}{r.mode ? ` · ${r.mode}` : ''}
                </p>
                {r.notes && <p className="text-xs text-gray-600 mt-1">“{r.notes}”</p>}
              </div>
            </div>
            {r.status === 'Pending' && (
              <>
                <p className={`text-xs rounded-xl px-3 py-2 ${what.warn ? 'bg-orange-50 text-orange-800' : 'bg-gray-50 text-gray-600'}`}>{what.text}</p>
                {turningDown === r.id ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why not (they will see this)" className={inputCls} autoFocus aria-label="Reason" />
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => decide(r, false)} disabled={!!busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{busy === r.id ? 'Saving…' : 'Turn down'}</button>
                      <button onClick={() => { setTurningDown(null); setReason(''); }} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => decide(r, true)} disabled={!!busy || r.existing?.stage === 'Converted'} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>
                      {busy === r.id ? 'Approving…' : `Approve for ${r.byName}`}
                    </button>
                    <button onClick={() => { setTurningDown(r.id); setReason(''); }} disabled={!!busy} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">Turn down</button>
                  </div>
                )}
              </>
            )}
            {r.status === 'Approved' && <p className="text-xs text-green-700">Lead #{r.leadNo} · approved by {r.decidedByName} {r.decidedAt ? fmtWhen(r.decidedAt) : ''}</p>}
            {r.status === 'Rejected' && <p className="text-xs text-red-600">Turned down by {r.decidedByName}{r.reason ? `: ${r.reason}` : ''}</p>}
          </div>
        );
      })}
    </div>
  );
};
